import { Component, EventEmitter, forwardRef, Input, Output, SimpleChanges } from '@angular/core';
import { FormControl, FormGroup, NG_VALUE_ACCESSOR, ValidationErrors, Validators } from '@angular/forms';
import { UntilDestroy } from '@ngneat/until-destroy';
import { FormsUtil } from 'mbs-ui-kit';
import { RestoreAsDiskEnum, TypeDataStepValue, TypeDataStepValueForm } from 'wizards/models/type-data-models';
import { RemoteManagementWizardsService } from 'wizards/services/remote-management-wizards.service';
import { WizardStepsService } from 'wizards/services/wizard-steps.service';
import { StepBase } from 'wizards/steps/StepBase.class';

const TypeDataStepValueAccessor: any = {
  provide: NG_VALUE_ACCESSOR,
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  useExisting: forwardRef(() => TypeDataStepComponent),
  multi: true
};

@UntilDestroy()
@Component({
  selector: 'mbs-type-data-step',
  templateUrl: './type-data-step.component.html',
  providers: [TypeDataStepValueAccessor]
})
export class TypeDataStepComponent extends StepBase<TypeDataStepValue> {
  @Input() fileFormats = [];
  @Input() selectedComputersCount = 0;
  @Output() restoreAsChange = new EventEmitter();

  public readonly restoreAsDiskEnum = RestoreAsDiskEnum;
  public readonly elementsSelector = {
    name: {
      restoreHyperV: 'restoreHyperV',
      restoreVMWare: 'restoreVMWare',
      notVMRestore: 'notVMRestore',
      multipleMachineAlert: 'multipleMachineAlert'
    }
  };

  private updateCount = 0;
  private typesForValidate = [RestoreAsDiskEnum.RestoreToHyperVAndImport, RestoreAsDiskEnum.RestoreToVMware];

  get disableRestoreAsVirtual(): boolean {
    return this.isEdit && this.stepForm?.get('restoreAs')?.value !== RestoreAsDiskEnum.Virtual && !this.mainService.canChangeRestoreVMType;
  }

  get disableRestoreAsNotVirtual(): boolean {
    return this.isEdit && this.stepForm?.get('restoreAs')?.value === RestoreAsDiskEnum.Virtual && !this.mainService.canChangeRestoreVMType;
  }

  constructor(public mainService: RemoteManagementWizardsService, private stepService: WizardStepsService) {
    super(mainService);
  }

  ngOnInit(): void {
    this.initForm();

    if (this.isVMRestore) {
      this.stepService.hyperVNotFoundError$.pipe().subscribe({
        next: (error) => {
          this.stepForm.updateValueAndValidity();
          if (error && this.stepFocused) this.stepService.openErrorModal(error);
        }
      });
    }
  }

  initForm(): void {
    this.stepForm = new FormGroup<TypeDataStepValueForm>(
      {
        restoreAs: new FormControl(0),
        createAMI: new FormControl(false),
        selectedFileFormat: new FormControl('', [Validators.required])
      },
      this.hyperVNotFoundValidator.bind(this)
    );

    this.initFormEvents();
  }

  hyperVNotFoundValidator(): ValidationErrors | null {
    return this.typesForValidate.includes(this.stepForm?.get('restoreAs').value) && this.stepService.hyperVNotFoundError$?.value
      ? { hyperVNotFound: true }
      : null;
  }

  ngOnChanges(changes: SimpleChanges): void {
    if (changes?.selectedComputersCount) this.updateDataAfterChangeSelectedComputer(changes.selectedComputersCount.currentValue);
    if (
      changes?.stepFocused?.currentValue &&
      this.stepService.hyperVNotFoundError$.value &&
      this.typesForValidate.includes(this.stepForm?.get('restoreAs').value)
    ) {
      this.stepService.openErrorModal(this.stepService.hyperVNotFoundError$.value);
    }
  }

  ngOnDestroy(): void {
    if (this.isVMRestore) this.stepService.hyperVNotFoundError$?.next(null);
  }

  updateForm(value: TypeDataStepValue): void {
    if (!value.selectedFileFormat && this.fileFormats.length) value.selectedFileFormat = this.fileFormats[0];

    this.updateCount++;

    this.stepForm.reset(value);

    this.changeRestoreAsHandler(value.restoreAs);
  }

  forceValid(data: any = null): void {
    if (
      this.typesForValidate.includes(this.stepForm?.get('restoreAs').value) &&
      this.stepService.hyperVNotFoundError$?.value &&
      (data.isNext || data.isSave)
    ) {
      this.stepService.openErrorModal(this.stepService.hyperVNotFoundError$?.value);
    }

    FormsUtil.triggerValidation(this.stepForm);
    this.resetValidStateForValidFields();
  }

  changeRestoreAsHandler(event: RestoreAsDiskEnum): void {
    this.toggleFormControls(['createAMI'], event === RestoreAsDiskEnum.EC2);
    this.toggleFormControls(['selectedFileFormat'], event === RestoreAsDiskEnum.Virtual);

    this.restoreAsChange.emit(event);
  }

  private updateDataAfterChangeSelectedComputer(count: number): void {
    if (!this.stepForm || !this.isVMRestore) return;

    const control = this.stepForm.get('restoreAs');
    const needUpdate = count > 1 && control.value === RestoreAsDiskEnum.Virtual;

    if (!needUpdate) return;

    if (this.isCreate || this.updateCount > 1) {
      control.setValue(this.isRestoreHyperV ? RestoreAsDiskEnum.RestoreToHyperVAndImport : RestoreAsDiskEnum.RestoreToVMware);
    }

    this.restoreAsChange.emit(control.value);
  }
}
